import { cn } from "@/lib/cn";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import { EditorContent, mergeAttributes, Node as TiptapNode, useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/Button";
import { emptyHtml, templateToTokens, tokensToTemplate } from "@/lib/format";

export type PlaceholderField = { name: string; label: string };

type HtmlEditorProps = {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** Current placeholder values; the chips show these instead of `{{key}}`. */
  values?: Record<string, string>;
  /** Fields offered by the toolbar's insert menu. */
  fields?: PlaceholderField[];
  /** Click on a chip — the page focuses the matching field. */
  onPickPlaceholder?: (name: string) => void;
  /** Extra toolbar content (e.g. AI actions), rendered just before the `{{ }}` insert menu. */
  toolbarExtra?: ReactNode;
};

/**
 * Inline atom for `{{key}}`. It only ever lives inside the editor: `value` in
 * and out of this component stays the `{{key}}` template the backend renders.
 */
export const PlaceholderToken = TiptapNode.create({
  name: "placeholderToken",
  group: "inline",
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      name: {
        default: "",
        parseHTML: (element: HTMLElement) => element.getAttribute("data-placeholder") ?? "",
        renderHTML: (attributes: Record<string, unknown>) => ({ "data-placeholder": attributes.name }),
      },
      label: {
        default: "",
        parseHTML: (element: HTMLElement) => element.textContent ?? "",
        renderHTML: () => ({}),
      },
      filled: {
        default: false,
        parseHTML: (element: HTMLElement) => element.getAttribute("data-filled") === "1",
        renderHTML: (attributes: Record<string, unknown>) => ({ "data-filled": attributes.filled ? "1" : "0" }),
      },
    };
  },

  parseHTML() {
    return [{ tag: "span[data-placeholder]" }];
  },

  renderHTML({ HTMLAttributes, node }: { HTMLAttributes: Record<string, unknown>; node: { attrs: Record<string, unknown> } }) {
    const name = String(node.attrs.name || "");
    return [
      "span",
      mergeAttributes(HTMLAttributes, {
        class: "ph-token",
        title: `Platzhalter {{${name}}} — klicken zum Ändern`,
      }),
      String(node.attrs.label || `{{${name}}}`),
    ];
  },
});

export function labelFor(name: string, values: Record<string, string>, fields: PlaceholderField[]) {
  const value = values[name]?.trim();
  if (value) return { label: value, filled: true };
  const field = fields.find((item) => item.name === name);
  return { label: `‹${field?.label ?? name}›`, filled: false };
}

function ToolbarButton({
  active,
  disabled,
  onClick,
  children,
  className,
}: {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: string;
  className?: string;
}) {
  return (
    <Button
      type="button"
      size="sm"
      variant={active ? "primary" : "ghost"}
      disabled={disabled}
      onClick={onClick}
      className={cn("h-6 min-w-[1.6rem] px-1.5 font-mono text-[11px]", className)}
    >
      {children}
    </Button>
  );
}

export function HtmlEditor({
  value,
  onChange,
  disabled,
  placeholder,
  className,
  values,
  fields,
  onPickPlaceholder,
  toolbarExtra,
}: HtmlEditorProps) {
  const phValues = values ?? {};
  const phFields = fields ?? [];
  const [insertOpen, setInsertOpen] = useState(false);
  const insertRef = useRef<HTMLDivElement>(null);
  // Read inside TipTap callbacks, which capture the first render's props.
  const renderRef = useRef<(name: string) => { label: string; filled: boolean }>(() => ({
    label: "",
    filled: false,
  }));
  renderRef.current = (name: string) => labelFor(name, phValues, phFields);
  const pickRef = useRef(onPickPlaceholder);
  pickRef.current = onPickPlaceholder;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: false,
        code: false,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
      PlaceholderToken,
      Placeholder.configure({ placeholder: placeholder ?? "" }),
    ],
    content: templateToTokens(value || "", (name) => renderRef.current(name)),
    editable: !disabled,
    editorProps: {
      attributes: {
        class: "tiptap px-3 py-2",
      },
      handleClickOn: (_view, _pos, node) => {
        if (node.type.name !== "placeholderToken") return false;
        const name = String(node.attrs.name || "");
        if (!name || !pickRef.current) return false;
        pickRef.current(name);
        return true;
      },
    },
    onUpdate: ({ editor: instance }) => {
      onChange(tokensToTemplate(instance.getHTML()));
    },
  });

  useEffect(() => {
    function onDoc(event: MouseEvent) {
      if (!insertRef.current?.contains(event.target as Node)) setInsertOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  useEffect(() => {
    if (!editor) return;
    const current = tokensToTemplate(editor.getHTML());
    if (emptyHtml(value) && emptyHtml(current)) {
      return;
    }
    if (value !== current) {
      editor.commands.setContent(
        templateToTokens(value || "", (name) => renderRef.current(name)),
        false,
      );
    }
  }, [value, editor]);

  // Values changed elsewhere (the Eckdaten rail): relabel the chips in place so
  // the caret survives.
  useEffect(() => {
    if (!editor) return;
    const updates: { pos: number; label: string; filled: boolean }[] = [];
    editor.state.doc.descendants((node, pos) => {
      if (node.type.name !== "placeholderToken") return;
      const next = labelFor(String(node.attrs.name || ""), phValues, phFields);
      if (node.attrs.label !== next.label || node.attrs.filled !== next.filled) {
        updates.push({ pos, ...next });
      }
    });
    if (!updates.length) return;
    const { tr } = editor.state;
    for (const update of updates) {
      const node = editor.state.doc.nodeAt(update.pos);
      if (!node) continue;
      tr.setNodeMarkup(update.pos, undefined, {
        ...node.attrs,
        label: update.label,
        filled: update.filled,
      });
    }
    tr.setMeta("addToHistory", false);
    editor.view.dispatch(tr);
  }, [editor, values, fields]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  if (!editor) {
    return <div className="min-h-[140px] rounded-md border border-field-border bg-surface" />;
  }

  function insert(instance: Editor, name: string) {
    const { label, filled } = renderRef.current(name);
    instance
      .chain()
      .focus()
      .insertContent({ type: "placeholderToken", attrs: { name, label, filled } })
      .insertContent(" ")
      .run();
    setInsertOpen(false);
  }

  return (
    <div
      className={cn(
        "overflow-hidden rounded-md border border-field-border bg-surface transition-colors focus-within:border-accent",
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-0.5 border-b border-hairline bg-surface-subtle px-1 py-0.5">
        <ToolbarButton
          active={editor.isActive("bold")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          B
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("italic")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          I
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("heading", { level: 2 })}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        >
          H2
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("heading", { level: 3 })}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        >
          H3
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("bulletList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBulletList().run()}
        >
          •
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("orderedList")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
        >
          1.
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("blockquote")}
          disabled={disabled}
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
        >
          „
        </ToolbarButton>
        <ToolbarButton
          active={editor.isActive("link")}
          disabled={disabled}
          onClick={() => {
            const previous = String(editor.getAttributes("link").href ?? "");
            const url = window.prompt("URL", previous || "https://");
            if (url === null) return;
            if (url === "") {
              editor.chain().focus().unsetLink().run();
              return;
            }
            editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
          }}
        >
          Link
        </ToolbarButton>
        <ToolbarButton
          disabled={disabled}
          onClick={() => editor.chain().focus().insertTable({ rows: 2, cols: 2, withHeaderRow: true }).run()}
        >
          Tabelle
        </ToolbarButton>
        {toolbarExtra}
        {phFields.length ? (
          <div className="relative ml-auto" ref={insertRef}>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={disabled}
              aria-expanded={insertOpen}
              onClick={() => setInsertOpen((open) => !open)}
              className="h-6 px-1.5 font-mono text-[11px] text-accent"
            >
              {"{{ }}"}
            </Button>
            {insertOpen ? (
              <div className="absolute right-0 z-20 mt-1 min-w-[11rem] rounded-md border border-hairline bg-surface py-1 shadow-lg">
                {phFields.map((field) => (
                  <button
                    key={field.name}
                    type="button"
                    className="flex w-full items-baseline justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-surface-subtle"
                    onClick={() => insert(editor, field.name)}
                  >
                    {field.label}
                    <span className="font-mono text-[10px] text-muted">{`{{${field.name}}}`}</span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
      <EditorContent editor={editor} />
    </div>
  );
}
